import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bell, Calendar as CalIcon, CheckCircle2, Circle, ClipboardList, Pencil, Plus, Trash2, ExternalLink, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { plannerAPI, sundayOf, ymd, addDays, formatTime } from "@/lib/planner";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * PlannerCalendar — the "Planner" nav destination. Quick-add scratch pad for
 * events, tasks, and reminders, plus the Family Command Center share dialog.
 *
 * The URL param `?add=1` (fired by the mobile "+" and Week/Today's "+ Add"
 * links) auto-opens the add dialog; `?date=YYYY-MM-DD` pre-fills the date.
 */
export default function PlannerCalendar() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(sundayOf(new Date()));
  const [week, setWeek] = useState({ events: [], by_day: {} });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(params.get("add") === "1");
  const [editingEvent, setEditingEvent] = useState(null); // event doc or null
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState(null);

  const load = async (w = weekStart) => {
    setLoading(true);
    try {
      const d = await plannerAPI.week(ymd(w));
      setWeek(d);
    } catch { toast.error("Couldn't load your planner."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [weekStart]);

  useEffect(() => {
    // Load existing share info so the share button reflects state
    plannerAPI.share().then(setShare).catch(() => {});
  }, []);

  const closeDialog = () => {
    setDialogOpen(false);
    // Clean up the `?add=1` param so refresh doesn't reopen
    const p = new URLSearchParams(params);
    p.delete("add"); p.delete("date");
    setParams(p, { replace: true });
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await plannerAPI.deleteEvent(ev.id);
      setWeek((prev) => ({
        ...prev,
        events: (prev.events || []).filter((e) => e.id !== ev.id),
      }));
      toast.success("Deleted.");
    } catch { toast.error("Couldn't delete."); }
  };

  const rotateShare = async () => {
    try {
      const d = await plannerAPI.rotateShare();
      setShare(d);
      toast.success(share?.token ? "New link generated — old link is now revoked." : "Share link ready.");
    } catch { toast.error("Couldn't create a share link."); }
  };
  const revokeShare = async () => {
    if (!window.confirm("Revoke the current family link? Anyone with it will lose access.")) return;
    try { await plannerAPI.revokeShare(); setShare({ token: null }); toast.success("Share link revoked."); }
    catch { toast.error("Couldn't revoke."); }
  };

  const publicUrl = share?.token ? `${window.location.origin}/planner/shared/${share.token}` : "";

  return (
    <div data-testid="planner-calendar-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">PLANNER</div>
          <h1 className="text-3xl sm:text-4xl text-[#2A2823] mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Quick add + Family Command Center
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShareOpen(true)} data-testid="planner-share-open" variant="outline" className="rounded-full border-[#A87F56]/40 text-[#3E4A37] hover:bg-[#A87F56]/10 bg-transparent h-10">
            <Share2 className="w-4 h-4 mr-1.5" /> Family share
          </Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="planner-add-open" className="rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] h-10 px-5">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Simple week overview to give this page anchor content */}
      <div className="bg-white rounded-2xl border border-[#A87F56]/25 p-6">
        <div className="text-[10px] tracking-[0.35em] text-[#A87F56] mb-4">THIS WEEK</div>
        {loading ? (
          <div className="text-sm text-[#2A2823]/50 py-6 text-center">Loading…</div>
        ) : week.events.length === 0 ? (
          <div className="text-sm text-[#2A2823]/60 italic py-8 text-center">
            Nothing scheduled this week. Add your first event above.
          </div>
        ) : (
          <ul className="divide-y divide-[#A87F56]/15">
            {week.events.slice(0, 30).map((e) => (
              <li
                key={e.id}
                className="py-2.5 flex items-center gap-3 text-sm"
                data-testid={`planner-calendar-row-${e.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#EDE5DA] flex items-center justify-center shrink-0">
                  {e.event_type === "reminder" ? <Bell className="w-4 h-4 text-[#3E4A37]" /> : e.event_type === "task" ? <ClipboardList className="w-4 h-4 text-[#3E4A37]" /> : <CalIcon className="w-4 h-4 text-[#3E4A37]" />}
                </div>
                {/* Middle column: on mobile the date + title stack so the
                    title has the full row width; on ≥sm they sit side by
                    side (date fixed 128px, title fills the rest). */}
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-3">
                  <div className="text-xs text-[#2A2823]/65 tabular-nums sm:w-32 sm:shrink-0">
                    {new Date(e.start_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}{" "}
                    · {e.all_day ? "All day" : formatTime(e.start_at)}
                  </div>
                  <div className={`flex-1 min-w-0 ${e.done_at ? "line-through text-[#2A2823]/40" : "text-[#2A2823]"}`}>
                    <div className="truncate">{e.title}</div>
                  </div>
                </div>
                {e.remind_by_email && <Bell className="w-3.5 h-3.5 text-[#A87F56] shrink-0" />}
                <a
                  href={plannerAPI.eventIcsUrl(e.id)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Add to calendar"
                  className="text-[#2A2823]/40 hover:text-[#3E4A37] shrink-0"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button
                  onClick={() => setEditingEvent(e)}
                  aria-label="Edit event"
                  data-testid={`planner-calendar-edit-${e.id}`}
                  className="text-[#2A2823]/40 hover:text-[#3E4A37] shrink-0"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteEvent(e)}
                  aria-label="Delete event"
                  data-testid={`planner-calendar-delete-${e.id}`}
                  className="text-[#2A2823]/40 hover:text-[#8a3a2a] shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Quick-add dialog */}
      <AddEventDialog
        open={dialogOpen}
        onClose={closeDialog}
        defaultDate={params.get("date") || ymd(new Date())}
        onSaved={() => { closeDialog(); load(); }}
      />

      {/* Edit dialog — reused from EventDialog with `event` prop set */}
      <EventDialog
        open={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        defaultDate={ymd(new Date())}
        event={editingEvent}
        onSaved={() => { setEditingEvent(null); load(); }}
      />

      {/* Family share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-lg bg-[#F7F3ED] border-[#A87F56]/30" data-testid="planner-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2A2823]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Family Command Center
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#2A2823]/70 leading-relaxed">
            Share a read-only view of your Today, This Week, and Meal Plan with family. No accounts needed — anyone with the link can see it. You can rotate or revoke the link anytime.
          </p>
          {share?.token ? (
            <div className="mt-4 space-y-3">
              <div className="rounded-2xl bg-white border border-[#A87F56]/25 p-3 flex items-center gap-2">
                <input
                  data-testid="planner-share-url"
                  readOnly
                  value={publicUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 bg-transparent text-sm text-[#2A2823] outline-none"
                />
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(publicUrl);
                      toast.success("Link copied.");
                    } catch {
                      // Fallback for browsers that block the async API — highlight
                      // the input so the user can Cmd/Ctrl+C without a false success.
                      const el = document.querySelector('[data-testid="planner-share-url"]');
                      if (el) { el.focus(); el.select(); }
                      toast.error("Couldn't copy — the link is highlighted; press Cmd/Ctrl+C to copy.");
                    }
                  }}
                  data-testid="planner-share-copy"
                  className="rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] h-8"
                >
                  Copy
                </Button>
              </div>
              <div className="flex gap-2">
                <Button onClick={rotateShare} variant="outline" data-testid="planner-share-rotate" className="rounded-full border-[#A87F56]/40 text-[#3E4A37] hover:bg-[#A87F56]/10 bg-transparent">
                  Rotate link
                </Button>
                <Button onClick={revokeShare} variant="outline" data-testid="planner-share-revoke" className="rounded-full border-[#8a3a2a]/40 text-[#8a3a2a] hover:bg-[#8a3a2a]/10 bg-transparent">
                  Revoke
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <Button onClick={rotateShare} data-testid="planner-share-create" className="rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] h-11 px-6">
                Create share link
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * EventDialog — inline dialog for creating OR editing an event / task /
 * reminder. Pass an `event` prop to edit an existing row; omit it (or pass
 * null) to create a new one from scratch.
 *
 * Kept alongside PlannerCalendar as an internal helper — PlannerToday
 * imports it via the re-export below.
 */
export function EventDialog({ open, onClose, defaultDate, event, onSaved }) {
  const isEditing = !!event;
  const [title, setTitle] = useState("");
  const [eventType, setEventType] = useState("event");
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState("09:00");
  const [allDay, setAllDay] = useState(false);
  const [remindByEmail, setRemindByEmail] = useState(false);
  const [remindOffset, setRemindOffset] = useState(60);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Seed the form when the dialog opens. In edit mode we pull every value
  // from the existing event doc; in create mode we reset to defaults.
  useEffect(() => {
    if (!open) return;
    if (isEditing) {
      const d = event.start_at ? new Date(event.start_at) : new Date();
      const pad = (n) => String(n).padStart(2, "0");
      setTitle(event.title || "");
      setEventType(event.event_type || "event");
      setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
      setTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
      setAllDay(!!event.all_day);
      setRemindByEmail(!!event.remind_by_email);
      setRemindOffset(event.remind_offset_minutes ?? 60);
      setNotes(event.notes || "");
    } else {
      setTitle(""); setEventType("event"); setDate(defaultDate);
      setTime("09:00"); setAllDay(false); setRemindByEmail(false); setRemindOffset(60); setNotes("");
    }
  }, [open, defaultDate, isEditing, event]);

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      // Build a local ISO datetime, then let Date normalise to a tz-aware ISO.
      const iso = allDay
        ? new Date(date + "T00:00:00").toISOString()
        : new Date(`${date}T${time}:00`).toISOString();
      const payload = {
        title: title.trim(),
        event_type: eventType,
        start_at: iso,
        all_day: allDay,
        remind_by_email: remindByEmail,
        remind_offset_minutes: remindOffset,
        notes: notes.trim() || null,
      };
      if (isEditing) {
        await plannerAPI.updateEvent(event.id, payload);
        toast.success("Event updated.");
      } else {
        await plannerAPI.createEvent(payload);
        toast.success("Added.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEditing ? "Couldn't update." : "Couldn't save."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#F7F3ED] border-[#A87F56]/30" data-testid={isEditing ? "planner-edit-dialog" : "planner-add-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#2A2823]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {isEditing ? "Edit event" : "Add to your Planner"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2823]/60">What is it?</label>
            <input
              data-testid="planner-add-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. School drop-off"
              autoFocus
              className="mt-1 w-full rounded-full border border-[#A87F56]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/25"
              required
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {["event", "task", "reminder"].map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`planner-add-type-${t}`}
                onClick={() => setEventType(t)}
                className={`rounded-full px-4 h-8 text-sm border ${
                  eventType === t
                    ? "bg-[#3E4A37] text-[#F7F3ED] border-[#3E4A37]"
                    : "border-[#A87F56]/40 text-[#2A2823]/75 hover:bg-[#A87F56]/10"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2823]/60">Date</label>
              <input
                data-testid="planner-add-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 w-full rounded-full border border-[#A87F56]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/25"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2823]/60">Time</label>
              <input
                data-testid="planner-add-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                disabled={allDay}
                className="mt-1 w-full rounded-full border border-[#A87F56]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/25 disabled:opacity-50"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#2A2823]/80">
            <input
              type="checkbox"
              data-testid="planner-add-all-day"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="accent-[#3E4A37]"
            />
            All-day
          </label>

          <div className="rounded-2xl bg-white/60 border border-[#A87F56]/25 p-3">
            <label className="flex items-center gap-2 text-sm text-[#2A2823]/80">
              <input
                type="checkbox"
                data-testid="planner-add-remind"
                checked={remindByEmail}
                onChange={(e) => setRemindByEmail(e.target.checked)}
                className="accent-[#3E4A37]"
              />
              Email me a reminder
            </label>
            {remindByEmail && (
              <div className="mt-2 flex items-center gap-2 text-sm text-[#2A2823]/70">
                <select
                  data-testid="planner-add-remind-offset"
                  value={remindOffset}
                  onChange={(e) => setRemindOffset(Number(e.target.value))}
                  className="rounded-full border border-[#A87F56]/40 bg-white px-3 h-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/25"
                >
                  {[15, 30, 60, 120, 240, 1440].map((m) => (
                    <option key={m} value={m}>{m < 60 ? `${m} min before` : m === 60 ? "1 hour before" : m === 1440 ? "1 day before" : `${m / 60} hours before`}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2823]/60">Notes (optional)</label>
            <textarea
              data-testid="planner-add-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-2xl border border-[#A87F56]/40 bg-white px-4 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#3E4A37]/25"
              placeholder="Anything else"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full border-[#A87F56]/40 text-[#2A2823] hover:bg-[#A87F56]/10 bg-transparent">
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !title.trim()} data-testid={isEditing ? "planner-edit-submit" : "planner-add-submit"} className="rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] px-6">
              {saving ? (isEditing ? "Saving…" : "Adding…") : (isEditing ? "Save changes" : "Add")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Backwards-compatible shim — the original AddEventDialog is kept as a thin
 * wrapper around EventDialog so any code still importing it keeps working.
 */
function AddEventDialog(props) {
  return <EventDialog {...props} event={null} />;
}
